<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-4xl sm:p-6">
              
              <!-- Header -->
              <div class="flex justify-between items-start mb-6">
                <div>
                  <h2 class="text-xl font-bold leading-9 tracking-tight text-gray-900">Visualizar Matriz de Reenquadramento #{{ planoId }}</h2>
                  <p class="text-sm text-gray-500 mt-1">
                    Insira regras manualmente ou utilize as ações de upload de Excel para importar em massa.
                  </p>
                </div>
                <button @click="closeDialog" type="button" class="text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 rounded-full p-1 -mt-4">
                  <span class="sr-only">Fechar</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="flex justify-center items-center py-10">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <span class="ml-3 text-sm text-gray-600">Buscando a matriz no servidor...</span>
              </div>

              <div v-else class="space-y-8">
                
                <!-- Matriz de Reenquadramentos -->
                <div>
                  <div class="flex justify-between items-center border-b border-gray-200 pb-2 mb-4">
                    <h3 class="text-lg font-semibold text-emerald-700">Regras de Reenquadramento</h3>
                    <button @click="showAddReenq = !showAddReenq" class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-bold rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors">
                      {{ showAddReenq ? 'Cancelar' : '+ Adicionar Regra' }}
                    </button>
                  </div>
                  
                  <div class="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Idade Min (meses)</th>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Idade Max (meses)</th>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capital Min</th>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capital Max</th>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reajuste</th>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                          <th scope="col" class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-if="reenquadramentos.length === 0 && !showAddReenq">
                          <td colspan="7" class="px-6 py-10 text-center text-sm text-gray-500">Nenhuma Matriz de Reenquadramento encontrada para este plano.</td>
                        </tr>
                        
                        <!-- Formulario Add Row -->
                        <tr v-if="showAddReenq" class="bg-emerald-50 border-b-2 border-emerald-200">
                          <td class="px-2 py-3"><input type="number" v-model="newReenq.idade_min_meses" placeholder="Mín" class="w-full text-sm border-gray-300 rounded p-1.5 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"/></td>
                          <td class="px-2 py-3"><input type="number" v-model="newReenq.idade_max_meses" placeholder="Máx" class="w-full text-sm border-gray-300 rounded p-1.5 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"/></td>
                          <td class="px-2 py-3"><input type="number" v-model="newReenq.capital_min" placeholder="Cap. Mín" class="w-full text-sm border-gray-300 rounded p-1.5 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"/></td>
                          <td class="px-2 py-3"><input type="number" v-model="newReenq.capital_max" placeholder="Cap. Máx" class="w-full text-sm border-gray-300 rounded p-1.5 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"/></td>
                          <td class="px-2 py-3">
                            <select v-model="newReenq.tipo_reajuste" class="w-full text-sm border-gray-300 rounded p-1.5 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm">
                              <option value="percentual">% Percentual</option>
                              <option value="fixo_monetario">R$ Monetário</option>
                            </select>
                          </td>
                          <td class="px-2 py-3"><input type="number" step="any" v-model="newReenq.valor_reajuste" placeholder="Valor" class="w-full text-sm border-gray-300 rounded p-1.5 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"/></td>
                          <td class="px-4 py-3 text-right">
                            <button @click="saveReenq" :disabled="loadingAction" class="inline-flex items-center px-4 py-1.5 border border-transparent text-xs font-bold rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50">
                              {{ loadingAction ? '...' : 'Salvar' }}
                            </button>
                          </td>
                        </tr>
                        <!-- Fim Formulario Add Row -->

                        <tr v-for="item in paginatedReenquadramentos" :key="item.id" class="hover:bg-gray-50 transition-colors">
                          <td class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-100">{{ item.idade_min_meses ?? '-' }}</td>
                          <td class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-100">{{ item.idade_max_meses ?? '-' }}</td>
                          <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{{ formatCurrency(item.capital_min) }}</td>
                          <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{{ formatCurrency(item.capital_max) }}</td>
                          <td class="px-4 py-4 whitespace-nowrap border-l border-gray-100">
                            <span :class="[
                              item.tipo_reajuste === 'percentual' ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-green-100 text-green-800 border border-green-200', 
                              'px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full'
                            ]">
                              {{ item.tipo_reajuste === 'percentual' ? 'Percentual' : 'Fixo' }}
                            </span>
                          </td>
                          <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {{ formatNumber(item.valor_reajuste) }}{{ item.tipo_reajuste === 'percentual' ? '%' : '' }}
                          </td>
                          <td class="px-4 py-4 whitespace-nowrap text-right text-sm">
                            <button @click="deleteReenq(item.id)" class="text-red-500 hover:text-red-700 font-semibold flex items-center justify-end w-full" :disabled="loadingAction">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Excluir
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination Reenquadramentos -->
                  <div class="flex items-center justify-between mt-4 bg-gray-50 p-3 rounded-lg border border-gray-200" v-if="reenquadramentos.length > reenquadramentosPerPage">
                    <button 
                      @click="currentReenqPage--" 
                      :disabled="currentReenqPage === 1"
                      class="inline-flex items-center px-4 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                      </svg>
                      Anterior
                    </button>
                    <div class="text-sm text-gray-600 font-medium">
                      Página <span class="text-emerald-700 font-bold">{{ currentReenqPage }}</span> de <span class="font-bold">{{ totalReenquadramentosPages }}</span>
                      <span class="text-xs text-gray-400 ml-2">({{ reenquadramentos.length }} registros)</span>
                    </div>
                    <button 
                      @click="currentReenqPage++" 
                      :disabled="currentReenqPage >= totalReenquadramentosPages"
                      class="inline-flex items-center px-4 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 transition-colors"
                    >
                      Próxima
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>

              </div>

            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import PlanoMatrizRepository from '@/services/PlanoMatrizRepository.vue';
import { handleUnauthorized } from '@/services/AuthSer/authServiceFunction';
import Swal from 'sweetalert2';

export default {
  name: 'MatrizesVisualizarModal',
  components: {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    planoId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingAction: false,
      showAddReenq: false,
      newReenq: { tipo_reajuste: 'percentual' },
      reenquadramentos: [],
      
      // Pagination para Reenquadramentos
      currentReenqPage: 1,
      reenquadramentosPerPage: 10,
    };
  },
  watch: {
    open(newVal) {
      if (newVal && this.planoId) {
        this.resetState();
        this.fetchData();
      }
    }
  },
  computed: {
    totalReenquadramentosPages() {
      return Math.ceil(this.reenquadramentos.length / this.reenquadramentosPerPage);
    },
    paginatedReenquadramentos() {
      const start = (this.currentReenqPage - 1) * this.reenquadramentosPerPage;
      const end = start + this.reenquadramentosPerPage;
      return this.reenquadramentos.slice(start, end);
    }
  },
  methods: {
    closeDialog() {
      this.$emit('update-open', false);
    },
    resetState() {
      this.reenquadramentos = [];
      this.currentReenqPage = 1;
      this.showAddReenq = false;
      this.newReenq = { tipo_reajuste: 'percentual' };
    },
    async fetchData() {
      this.loading = true;
      const user = JSON.parse(window.localStorage.getItem("myUser"));
      const repo = new PlanoMatrizRepository();
      
      try {
        const urlReenq = `${process.env.VUE_APP_API_URL}planos/${this.planoId}/matriz-reenquadramentos`;
        const resReenq = await repo.getReenquadramentos(urlReenq, user.token);
        
        if (resReenq && resReenq.data) {
          this.reenquadramentos = resReenq.data;
        }

      } catch (error) {
        handleUnauthorized(error);
        console.error("Erro ao buscar matriz de reenquadramento do plano:", error);
      } finally {
        this.loading = false;
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return 'R$ ' + Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async saveReenq() {
      if (!this.newReenq.valor_reajuste || !this.newReenq.tipo_reajuste) {
        Swal.fire('Erro', 'Preencha os campos obrigatórios (Tipo de Reajuste e Valor).', 'error');
        return;
      }
      this.loadingAction = true;
      const repo = new PlanoMatrizRepository();
      const user = JSON.parse(window.localStorage.getItem("myUser"));
      try {
        const url = `${process.env.VUE_APP_API_URL}planos/${this.planoId}/matriz-reenquadramentos`;
        await repo.createReenquadramento(url, this.newReenq, user.token);
        Swal.fire('Sucesso', 'Regra de Reenquadramento adicionada.', 'success');
        this.showAddReenq = false;
        this.newReenq = { tipo_reajuste: 'percentual' };
        this.fetchData();
      } catch (error) {
        Swal.fire('Erro', error.response?.data?.message || 'Falha ao adicionar regra.', 'error');
      } finally {
        this.loadingAction = false;
      }
    },
    async deleteReenq(id) {
      const confirm = await Swal.fire({
        title: 'Certeza?',
        text: "Deseja remover esta regra de reenquadramento?",
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sim, excluir'
      });
      if (!confirm.isConfirmed) return;

      this.loadingAction = true;
      const repo = new PlanoMatrizRepository();
      const user = JSON.parse(window.localStorage.getItem("myUser"));
      try {
        const url = `${process.env.VUE_APP_API_URL}matriz-reenquadramentos/${id}`;
        await repo.deleteReenquadramento(url, user.token);
        this.fetchData();
        Swal.fire('Deletado', '', 'success');
      } catch (error) {
        Swal.fire('Erro', 'Falha ao remover regra.', 'error');
      } finally {
        this.loadingAction = false;
      }
    }
  }
};
</script>
